// frontend/src/services/VoiceBackupService.js
// BULLETPROOF VOICE BACKUP SERVICE - ZERO DATA LOSS GUARANTEE
// GPT5 SPECIFICATION: Progressive backup with IndexedDB + localStorage fallback

class VoiceBackupService {
  constructor() {
    this.dbName = 'ideaHiveVoiceBackup';
    this.dbVersion = 1;
    this.storeName = 'voiceRecordings';
    this.db = null;
    this.isIndexedDBSupported = true;
    this._quotaWarningShown = false; // Prevent warning spam

    this.init();
  }

  async init() {
    try {
      // Try to initialize IndexedDB
      await this.initIndexedDB();
      // console.log('[VoiceBackup] IndexedDB initialized successfully'); // DEBUG: Track IndexedDB
    } catch (error) {
      console.warn('[VoiceBackup] IndexedDB failed, falling back to localStorage:', error);
      this.isIndexedDBSupported = false;
    }
  }

  async initIndexedDB() {
    return new Promise((resolve, reject) => {
      if (!window.indexedDB) {
        reject(new Error('IndexedDB not supported'));
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Create voice recordings store
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('userId', 'userId', { unique: false });
          store.createIndex('status', 'status', { unique: false });
        }
      };
    });
  }

  /**
   * Start progressive backup session
   * Creates backup record immediately when recording starts
   */
  async startBackupSession(sessionId, userId, options = {}) {
    const backupData = {
      id: sessionId,
      userId,
      status: 'recording',
      startTime: Date.now(),
      lastUpdate: Date.now(),
      chunks: [],
      totalSize: 0,
      duration: 0,
      projectId: options.projectId || null,
      tier: options.tier || 'free',
      metadata: {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        ...options.metadata
      }
    };

    try {
      if (this.isIndexedDBSupported) {
        await this.saveToIndexedDB(backupData);
      }
      // Always save to localStorage as fallback
      this.saveToLocalStorage(sessionId, backupData);

      // console.log('[VoiceBackup] Backup session started:', sessionId); // DEBUG: Track session creation
      return backupData;
    } catch (error) {
      console.error('[VoiceBackup] Failed to start backup session:', error);
      throw error;
    }
  }

  /**
   * CRITICAL: Progressive chunk backup during recording
   * Called every few seconds while recording to prevent data loss
   */
  async addChunk(sessionId, chunkBlob, duration) {
    // Defensive check: Return early if sessionId is falsy
    if (!sessionId) {
      console.warn('[VoiceBackup] addChunk called with falsy sessionId, skipping');
      return null;
    }

    try {
      // Convert blob to base64 for storage
      const base64Chunk = await this.blobToBase64(chunkBlob);

      const backup = await this.getBackup(sessionId);
      if (!backup) {
        throw new Error(`Backup session ${sessionId} not found`);
      }

      // Add chunk to backup
      backup.chunks.push({
        data: base64Chunk,
        size: chunkBlob.size,
        timestamp: Date.now(),
        duration: duration
      });

      backup.totalSize += chunkBlob.size;
      backup.duration = duration;
      backup.lastUpdate = Date.now();

      // Save updated backup
      if (this.isIndexedDBSupported) {
        await this.saveToIndexedDB(backup);
      }
      this.saveToLocalStorage(sessionId, backup);

      // console.log(`[VoiceBackup] Chunk added: ${chunkBlob.size} bytes, total: ${backup.totalSize} bytes`); // DEBUG: Track chunk backup
      return backup;
    } catch (error) {
      console.error('[VoiceBackup] Failed to add chunk:', error);
      // Don't throw - recording should continue even if backup fails
    }
  }

  /**
   * Complete backup session with final audio blob
   */
  async completeBackup(sessionId, finalBlob, transcript = '') {
    // Defensive check: Return early if sessionId is falsy
    if (!sessionId) {
      console.warn('[VoiceBackup] completeBackup called with falsy sessionId, skipping');
      return null;
    }

    try {
      const backup = await this.getBackup(sessionId);
      if (!backup) {
        console.warn('[VoiceBackup] No backup found for session:', sessionId);
        return null;
      }

      // Store final audio blob
      const finalBase64 = await this.blobToBase64(finalBlob);
      backup.finalAudio = finalBase64;
      backup.transcript = transcript;
      backup.status = 'completed';
      backup.completedAt = Date.now();
      backup.finalSize = finalBlob.size;

      // Save completed backup
      if (this.isIndexedDBSupported) {
        await this.saveToIndexedDB(backup);
      }
      this.saveToLocalStorage(sessionId, backup);

      // console.log('[VoiceBackup] Backup completed:', sessionId); // DEBUG: Track completion
      return backup;
    } catch (error) {
      console.error('[VoiceBackup] Failed to complete backup:', error);
      throw error;
    }
  }

  /**
   * Save only metadata (no audio) to avoid localStorage quota issues
   * Used during recording to save progress without storing large audio blobs
   */
  async saveMetadataOnly(sessionId, metadata) {
    if (!sessionId) {
      console.warn('[VoiceBackup] saveMetadataOnly called with falsy sessionId');
      return null;
    }

    try {
      const backup = await this.getBackup(sessionId);
      if (!backup) {
        // Create minimal backup if doesn't exist
        const minimalBackup = {
          id: sessionId,
          status: 'recording',
          lastUpdate: Date.now(),
          ...metadata,
          chunks: [], // No chunks to save space
          totalSize: 0
        };

        // Only save to localStorage (IndexedDB may also be over quota)
        this.saveToLocalStorage(sessionId, minimalBackup);
        return minimalBackup;
      }

      // Update existing backup with new metadata (remove audio data)
      backup.duration = metadata.duration;
      backup.chunkCount = metadata.chunkCount;
      backup.transcript = metadata.transcript || backup.transcript;
      backup.projectId = metadata.projectId || backup.projectId;
      backup.lastUpdate = Date.now();

      // Remove large data before saving
      delete backup.chunks;
      delete backup.finalAudio;

      this.saveToLocalStorage(sessionId, backup);
      return backup;
    } catch (error) {
      console.error('[VoiceBackup] Failed to save metadata:', error);
      // Non-fatal - recording continues
    }
  }

  /**
   * Mark backup as uploaded successfully
   */
  async markUploaded(sessionId, voiceId, uploadResponse = {}) {
    try {
      const backup = await this.getBackup(sessionId);
      if (!backup) return null;

      backup.status = 'uploaded';
      backup.voiceId = voiceId;
      backup.uploadedAt = Date.now();
      backup.uploadResponse = uploadResponse;

      if (this.isIndexedDBSupported) {
        await this.saveToIndexedDB(backup);
      }
      this.saveToLocalStorage(sessionId, backup);

      // Schedule cleanup after 24 hours
      setTimeout(() => this.cleanupBackup(sessionId), 24 * 60 * 60 * 1000);

      console.log('[VoiceBackup] Backup marked as uploaded:', sessionId);
      return backup;
    } catch (error) {
      console.error('[VoiceBackup] Failed to mark as uploaded:', error);
    }
  }

  /**
   * Get backup by session ID
   */
  async getBackup(sessionId) {
    try {
      if (this.isIndexedDBSupported) {
        const backup = await this.getFromIndexedDB(sessionId);
        if (backup) return backup;
      }

      // Fallback to localStorage
      return this.getFromLocalStorage(sessionId);
    } catch (error) {
      console.error('[VoiceBackup] Failed to get backup:', error);
      return null;
    }
  }

  /**
   * Get all failed/incomplete backups for recovery
   */
  async getFailedBackups(userId) {
    const failed = [];

    try {
      if (this.isIndexedDBSupported) {
        const indexedBackups = await this.getAllFromIndexedDB(userId);
        failed.push(...indexedBackups.filter(b =>
          b.status !== 'uploaded' && b.status !== 'deleted'
        ));
      }

      // Also check localStorage
      const localBackups = this.getAllFromLocalStorage();
      failed.push(...localBackups.filter(b =>
        b.userId === userId &&
        b.status !== 'uploaded' &&
        b.status !== 'deleted'
      ));

      // Remove duplicates by session ID
      const unique = failed.reduce((acc, current) => {
        if (!acc.find(item => item.id === current.id)) {
          acc.push(current);
        }
        return acc;
      }, []);

      console.log(`[VoiceBackup] Found ${unique.length} failed backups for recovery`);
      return unique;
    } catch (error) {
      console.error('[VoiceBackup] Failed to get failed backups:', error);
      return [];
    }
  }

  /**
   * Convert backup to downloadable blob
   */
  async getDownloadableBlob(sessionId) {
    try {
      const backup = await this.getBackup(sessionId);
      if (!backup) return null;

      // Use final audio if available, otherwise reconstruct from chunks
      if (backup.finalAudio) {
        return this.base64ToBlob(backup.finalAudio);
      }

      // Reconstruct from chunks
      if (backup.chunks && backup.chunks.length > 0) {
        const chunkBlobs = backup.chunks.map(chunk => this.base64ToBlob(chunk.data));
        return new Blob(chunkBlobs, { type: 'audio/webm' });
      }

      return null;
    } catch (error) {
      console.error('[VoiceBackup] Failed to get downloadable blob:', error);
      return null;
    }
  }

  /**
   * Clean up old backups to save space
   */
  async cleanupOldBackups(maxAge = 7 * 24 * 60 * 60 * 1000) { // 7 days
    try {
      const cutoff = Date.now() - maxAge;

      if (this.isIndexedDBSupported) {
        await this.cleanupIndexedDB(cutoff);
      }
      this.cleanupLocalStorage(cutoff);

      console.log('[VoiceBackup] Cleanup completed');
    } catch (error) {
      console.error('[VoiceBackup] Cleanup failed:', error);
    }
  }

  // Private helper methods
  async saveToIndexedDB(data) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(data);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getFromIndexedDB(sessionId) {
    return new Promise((resolve, reject) => {
      // Validate sessionId before attempting IndexedDB operation
      if (!sessionId) {
        console.error('[VoiceBackup] Cannot get backup: sessionId is undefined or null');
        reject(new Error('SessionId is required for IndexedDB lookup'));
        return;
      }

      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(sessionId);

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllFromIndexedDB(userId) {
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([this.storeName], 'readonly');
      const store = transaction.objectStore(this.storeName);
      const index = store.index('userId');
      const request = index.getAll(userId);

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  saveToLocalStorage(sessionId, data) {
    try {
      const key = `voice_backup_${sessionId}`;

      // CRITICAL FIX: ALWAYS save trimmed version to avoid quota issues
      try {
        const trimmedData = {
          id: data.id,
          userId: data.userId,
          status: data.status,
          startTime: data.startTime,
          lastUpdate: data.lastUpdate,
          duration: data.duration,
          projectId: data.projectId,
          transcript: data.transcript || '',
          chunkCount: data.chunks ? data.chunks.length : 0,
          finalSize: data.finalSize || 0,
          // NEVER save chunks or finalAudio to localStorage (too large)
        };

        localStorage.setItem(key, JSON.stringify(trimmedData));
      } catch (quotaError) {
        if (quotaError.name === 'QuotaExceededError') {
          // Only warn once per session to avoid spam
          if (!this._quotaWarningShown) {
            console.warn('[VoiceBackup] localStorage quota exceeded, recording continues in memory only');
            this._quotaWarningShown = true;
          }
          return; // Silent failure - not critical for recording
        } else {
          throw quotaError; // Re-throw non-quota errors
        }
      }

      // Also maintain a list of backup keys
      const backupKeys = JSON.parse(localStorage.getItem('voice_backup_keys') || '[]');
      if (!backupKeys.includes(key)) {
        backupKeys.push(key);
        try {
          localStorage.setItem('voice_backup_keys', JSON.stringify(backupKeys));
        } catch (keysError) {
          console.warn('[VoiceBackup] Could not update backup keys list:', keysError);
        }
      }
    } catch (error) {
      console.error('[VoiceBackup] LocalStorage save failed:', error);
      // Don't throw - backup failure should not crash recording
    }
  }

  getFromLocalStorage(sessionId) {
    try {
      const key = `voice_backup_${sessionId}`;
      const data = localStorage.getItem(key);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('[VoiceBackup] LocalStorage get failed:', error);
      return null;
    }
  }

  getAllFromLocalStorage() {
    try {
      const backupKeys = JSON.parse(localStorage.getItem('voice_backup_keys') || '[]');
      const backups = [];

      for (const key of backupKeys) {
        const data = localStorage.getItem(key);
        if (data) {
          backups.push(JSON.parse(data));
        }
      }

      return backups;
    } catch (error) {
      console.error('[VoiceBackup] LocalStorage getAll failed:', error);
      return [];
    }
  }

  async blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  base64ToBlob(base64) {
    try {
      const arr = base64.split(',');
      const mime = arr[0].match(/:(.*?);/)[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new Blob([u8arr], { type: mime });
    } catch (error) {
      console.error('[VoiceBackup] Base64 to blob conversion failed:', error);
      return null;
    }
  }

  async cleanupBackup(sessionId) {
    try {
      if (this.isIndexedDBSupported && this.db) {
        const transaction = this.db.transaction([this.storeName], 'readwrite');
        const store = transaction.objectStore(this.storeName);
        store.delete(sessionId);
      }

      const key = `voice_backup_${sessionId}`;
      localStorage.removeItem(key);

      const backupKeys = JSON.parse(localStorage.getItem('voice_backup_keys') || '[]');
      const updatedKeys = backupKeys.filter(k => k !== key);
      localStorage.setItem('voice_backup_keys', JSON.stringify(updatedKeys));

      console.log('[VoiceBackup] Cleanup completed for:', sessionId);
    } catch (error) {
      console.error('[VoiceBackup] Individual cleanup failed:', error);
    }
  }

  async cleanupIndexedDB(cutoff) {
    if (!this.db) return;

    const transaction = this.db.transaction([this.storeName], 'readwrite');
    const store = transaction.objectStore(this.storeName);
    const index = store.index('timestamp');
    const range = IDBKeyRange.upperBound(cutoff);

    return new Promise((resolve, reject) => {
      const request = index.openCursor(range);
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  }

  // CRITICAL FIX: Emergency cleanup for quota exceeded errors
  clearOldLocalStorageBackups() {
    try {
      const backupKeys = JSON.parse(localStorage.getItem('voice_backup_keys') || '[]');
      const now = Date.now();
      const oneHourAgo = now - (60 * 60 * 1000); // 1 hour ago
      let removedCount = 0;

      // Remove backups older than 1 hour
      for (const key of backupKeys) {
        try {
          const data = localStorage.getItem(key);
          if (data) {
            const backup = JSON.parse(data);
            if (backup.lastUpdate < oneHourAgo || backup.status === 'uploaded') {
              localStorage.removeItem(key);
              removedCount++;
            }
          }
        } catch (parseError) {
          // Remove corrupted backup entries
          localStorage.removeItem(key);
          removedCount++;
        }
      }

      // Update backup keys list
      const remainingKeys = backupKeys.filter(key => {
        try {
          return localStorage.getItem(key) !== null;
        } catch {
          return false;
        }
      });
      localStorage.setItem('voice_backup_keys', JSON.stringify(remainingKeys));

      console.log(`[VoiceBackup] Emergency cleanup removed ${removedCount} old backups`);
    } catch (error) {
      console.error('[VoiceBackup] Emergency cleanup failed:', error);
    }
  }

  cleanupLocalStorage(cutoff) {
    try {
      const backupKeys = JSON.parse(localStorage.getItem('voice_backup_keys') || '[]');
      const keepKeys = [];

      for (const key of backupKeys) {
        const data = localStorage.getItem(key);
        if (data) {
          const backup = JSON.parse(data);
          if (backup.lastUpdate > cutoff) {
            keepKeys.push(key);
          } else {
            localStorage.removeItem(key);
          }
        }
      }

      localStorage.setItem('voice_backup_keys', JSON.stringify(keepKeys));
    } catch (error) {
      console.error('[VoiceBackup] LocalStorage cleanup failed:', error);
    }
  }
}

// Export singleton instance
const voiceBackupService = new VoiceBackupService();
export default voiceBackupService;